import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CourseCandidate, courseCandidatesService } from '../../../services/courseCandidatesService';
import { DesignRun, designRunsService } from '../../../services/designRunsService';
import { Chip, SmallButton } from './editors';
import { DesignRunPage } from './DesignRunPage';
import { NewCourseModal } from './NewCourseModal';
import { RenderStatusPage } from './RenderStatusPage';

const DESIGN_TONES: Record<string, 'gray' | 'blue' | 'amber' | 'green' | 'red'> = {
  designing: 'blue', design_review: 'amber', approved: 'green', design_failed: 'red',
};

/** Course Design: design once, realize per target (Simon, Talaera). */
export function CourseDesignSection() {
  const [runs, setRuns] = useState<DesignRun[]>([]);
  const [candidates, setCandidates] = useState<CourseCandidate[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [view, setView] = useState<'design' | 'render'>('design');
  const [showNew, setShowNew] = useState(false);
  const [existingCourseId, setExistingCourseId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [runsResp, candResp] = await Promise.all([
        designRunsService.list(),
        courseCandidatesService.list(),
      ]);
      setRuns(runsResp.runs);
      setCandidates(candResp.courses);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const titleById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of candidates) map[c.course_id] = c.title;
    return map;
  }, [candidates]);

  const designedCourseIds = useMemo(() => new Set(runs.map((r) => r.course_id)), [runs]);
  const undesigned = candidates.filter((c) => !designedCourseIds.has(c.course_id));

  const startDesign = async (courseId: string) => {
    setBusy(true);
    setError(null);
    try {
      const { run_id } = await designRunsService.create(courseId);
      await refresh();
      setSelectedRunId(run_id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (selectedRunId) {
    const run = runs.find((r) => r.run_id === selectedRunId);
    const title = run ? titleById[run.course_id] ?? run.course_id : '';
    if (view === 'render') {
      return (
        <RenderStatusPage
          runId={selectedRunId}
          courseTitle={title}
          onBack={() => setView('design')}
        />
      );
    }
    return (
      <DesignRunPage
        runId={selectedRunId}
        courseTitle={title}
        onBack={() => { setSelectedRunId(null); refresh(); }}
        onOpenRender={() => setView('render')}
      />
    );
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Course Design</h2>
          <p className="text-sm text-gray-500">
            Design a course once — objectives, lessons, exercises, scripts — then realize it
            for each platform.
          </p>
        </div>
        <SmallButton tone="primary" onClick={() => setShowNew(true)}>New course</SmallButton>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}

      {undesigned.length > 0 && (
        <div className="flex items-center gap-2">
          <select value={existingCourseId} onChange={(e) => setExistingCourseId(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
            <option value="">Design an existing candidate…</option>
            {undesigned.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
          </select>
          <SmallButton disabled={!existingCourseId || busy}
            onClick={() => startDesign(existingCourseId)}>
            Start design
          </SmallButton>
        </div>
      )}

      <table className="min-w-full bg-white border border-gray-200 rounded-md text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b">
            <th className="px-3 py-2">Course</th>
            <th className="px-3 py-2">Design</th>
            <th className="px-3 py-2">Targets</th>
            <th className="px-3 py-2">Updated</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.run_id} onClick={() => { setSelectedRunId(run.run_id); setView('design'); }}
              className="border-b last:border-0 hover:bg-gray-50 cursor-pointer">
              <td className="px-3 py-2 text-gray-900">{titleById[run.course_id] ?? run.course_id}</td>
              <td className="px-3 py-2">
                <Chip tone={DESIGN_TONES[run.design_status] ?? 'gray'}>
                  {run.design_status} v{run.design_version}
                </Chip>
              </td>
              <td className="px-3 py-2 space-x-1">
                {(['simon', 'talaera'] as const).map((t) => {
                  const st = run.realizations?.[t]?.status;
                  if (!st) return <Chip key={t} tone="gray">{t} —</Chip>;
                  const stale = (run.realizations?.[t]?.design_version ?? 0) < run.design_version;
                  return (
                    <Chip key={t} tone={stale ? 'amber' : st === 'failed' ? 'red' : 'green'}>
                      {t} {stale ? 'stale' : st}
                    </Chip>
                  );
                })}
              </td>
              <td className="px-3 py-2 text-gray-500">{new Date(run.updatedAt).toLocaleString()}</td>
            </tr>
          ))}
          {runs.length === 0 && (
            <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">No designed courses yet.</td></tr>
          )}
        </tbody>
      </table>

      {showNew && (
        <NewCourseModal
          onClose={() => setShowNew(false)}
          onCreated={(courseId) => { setShowNew(false); startDesign(courseId); }}
        />
      )}
    </div>
  );
}
